import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Check, Copy, Eye, KeyRound, Plus, RefreshCw, Trash2, Webhook, X,
} from 'lucide-react';
import { ApiClientError, DataTable, EmptyState, StatusBadge, relativeTime, type Column } from '@riskpay/ui-components';
import { api, useAuth } from '../lib/client';
import { IntentTester } from '../components/IntentTester';

/* ==========================================================================
 *  Developer settings — API keys, webhooks, return-URL verification
 *
 *  Every create flow here returns a secret exactly once. That value lives in
 *  component state and is shown in a panel the user has to dismiss; it is
 *  never written to a query cache, because a cache is a place a secret
 *  survives a re-render and ends up in a devtools export.
 * ========================================================================== */

interface ApiKey {
  id: string;
  mode: 'TEST' | 'LIVE';
  label: string;
  public_key: string;
  secret_key: string;
  scopes: string[];
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface WebhookEndpoint {
  id: string;
  url: string;
  mode: 'TEST' | 'LIVE';
  events: string[] | string;
  is_active: number;
  consecutive_failures: number;
  created_at: string;
}

interface Delivery {
  id: string;
  event_type: string;
  status: string;
  attempt: number;
  response_status: number | null;
  duration_ms: number | null;
  created_at: string;
  url: string;
}

const SCOPE_LABELS: Record<string, string> = {
  'payments:read': 'Read payments',
  'payments:write': 'Create and capture payments',
  'refunds:write': 'Issue refunds',
  'payouts:write': 'Move money out',
  'settlements:read': 'Read settlements',
};

/** Payouts move money off the platform; it is worth a second look in the UI. */
const DANGEROUS = new Set(['payouts:write']);

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(value).catch(() => undefined);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      className="btn btn-secondary btn-sm shrink-0"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-brand-400" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function SecretReveal({ title, value, onDismiss }: { title: string; value: string; onDismiss: () => void }) {
  return (
    <div className="rounded-lg border border-brand-800 bg-brand-soft p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-content">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-content-muted">
            This is the only time it is shown. We keep a hash, not the value — if you lose it, the only remedy is a new
            key.
          </p>
          <div className="mt-3.5 flex items-center gap-2">
            <code className="numeral min-w-0 flex-1 truncate rounded border border-line bg-surface-inset px-3 py-2 text-xs text-content">
              {value}
            </code>
            <CopyButton value={value} />
          </div>
          <button type="button" onClick={onDismiss} className="btn btn-ghost btn-sm mt-3 px-1.5">
            I have stored it
          </button>
        </div>
      </div>
    </div>
  );
}

export function Developers() {
  const queryClient = useQueryClient();
  const session = useAuth((s) => s.session);
  const [revealed, setRevealed] = useState<{ title: string; value: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookMode, setWebhookMode] = useState<'TEST' | 'LIVE'>('TEST');
  const [error, setError] = useState<string | null>(null);

  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ data: ApiKey[]; available_scopes: string[] }>('/developer/keys'),
  });
  const hooks = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get<{ data: WebhookEndpoint[]; available_events: string[] }>('/developer/webhooks'),
  });
  const deliveries = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () => api.get<{ data: Delivery[] }>('/developer/webhooks/deliveries'),
    refetchInterval: 20_000,
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) => api.del(`/developer/keys/${id}`),
    onSuccess: () => {
      setConfirmRevoke(null);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not revoke the key.'),
  });

  const createWebhook = useMutation({
    mutationFn: () =>
      api.post<{ secret: string }>('/developer/webhooks', {
        url: webhookUrl,
        mode: webhookMode,
        events: hooks.data?.available_events ?? ['payment.success'],
      }),
    onSuccess: (result) => {
      setRevealed({ title: 'Your webhook signing secret', value: result.secret });
      setWebhookUrl('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not create the endpoint.'),
  });

  const rotateSecret = useMutation({
    mutationFn: (id: string) => api.post<{ secret: string }>(`/developer/webhooks/${id}/rotate`),
    onSuccess: (result) => setRevealed({ title: 'Your rotated signing secret', value: result.secret }),
  });

  const returnKey = useMutation({
    mutationFn: () => api.get<{ key: string }>('/developer/return-signing-key'),
    onSuccess: (result) => setRevealed({ title: 'Your return-URL signing key', value: result.key }),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not read the signing key.'),
  });

  const rows = keys.data?.data ?? [];
  const liveKeys = rows.filter((k) => k.mode === 'LIVE' && !k.revoked_at).length;
  const testKeys = rows.filter((k) => k.mode === 'TEST' && !k.revoked_at).length;

  const keyColumns: Column<ApiKey>[] = [
    {
      key: 'label',
      header: 'Key',
      render: (k) => (
        <div className="min-w-0">
          <p className={`text-sm ${k.revoked_at ? 'text-content-faint line-through' : 'text-content'}`}>{k.label}</p>
          <p className="numeral mt-1 truncate text-2xs text-content-faint">{k.public_key}</p>
        </div>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      render: (k) => (
        <span className={`badge ${k.mode === 'LIVE' ? 'badge-success' : 'badge-neutral'}`}>{k.mode.toLowerCase()}</span>
      ),
    },
    {
      key: 'scopes',
      header: 'Scopes',
      render: (k) => (
        <span className="text-xs text-content-subtle">
          {k.scopes?.includes('*') ? 'Full access' : `${k.scopes?.length ?? 0} scoped`}
        </span>
      ),
    },
    {
      key: 'secret',
      header: 'Secret',
      render: (k) => <span className="numeral text-xs text-content-subtle">{k.secret_key}</span>,
    },
    {
      key: 'used',
      header: 'Last used',
      render: (k) => (
        <span className="text-xs text-content-subtle">{k.last_used_at ? relativeTime(k.last_used_at) : 'Never'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (k) =>
        k.revoked_at ? (
          <span className="badge badge-danger">revoked</span>
        ) : confirmRevoke === k.id ? (
          <span className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => revokeKey.mutate(k.id)}
              disabled={revokeKey.isPending}
              className="btn btn-sm border-danger-ring bg-danger-soft text-danger-text hover:bg-danger-soft"
            >
              Revoke now
            </button>
            <button type="button" onClick={() => setConfirmRevoke(null)} className="btn btn-ghost btn-sm px-1.5">
              Keep
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmRevoke(k.id)}
            className="btn btn-ghost btn-sm text-content-subtle hover:text-danger"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" />
            Revoke
          </button>
        ),
    },
  ];

  return (
    <div className="space-y-10">
      {revealed && <SecretReveal {...revealed} onDismiss={() => setRevealed(null)} />}
      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {/* ---- API keys ---------------------------------------------------- */}
      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">API keys</h2>
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-content-muted">
              A secret key authenticates every server-to-server call and carries the powers you give it here. It belongs
              on your server and nowhere else — never in an app bundle or a browser.
            </p>
            <p className="mt-2 text-xs text-content-faint">
              <span className="numeral text-content-subtle">{testKeys}</span> test ·{' '}
              <span className="numeral text-content-subtle">{liveKeys}</span> live currently active
            </p>
          </div>
          {!creating && (
            <button type="button" onClick={() => { setCreating(true); setError(null); }} className="btn btn-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Create key
            </button>
          )}
        </div>

        {creating && (
          <CreateKey
            availableScopes={keys.data?.available_scopes ?? []}
            onClose={() => setCreating(false)}
            onCreated={(secret, label) => {
              setCreating(false);
              setError(null);
              setRevealed({ title: `Secret key for “${label}”`, value: secret });
              void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
            }}
          />
        )}

        <DataTable
          columns={keyColumns}
          rows={rows}
          rowKey={(k) => k.id}
          loading={keys.isLoading}
          empty={
            <EmptyState
              title="No API keys yet"
              description="Create a test key to start integrating. Live keys unlock once compliance approves the account."
            />
          }
        />
      </section>

      {/* ---- Sandbox tester ------------------------------------------------ */}
      <IntentTester />

      {/* ---- Webhooks ---------------------------------------------------- */}
      <section>
        <div className="mb-4 border-b border-line pb-3">
          <h2 className="text-lg font-semibold tracking-tight">Webhook endpoints</h2>
          <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-content-muted">
            HTTPS only. Every delivery is signed with HMAC SHA-256 over{' '}
            <code className="numeral text-xs text-content-subtle">timestamp.body</code> — verify it before you trust the
            payload.
          </p>
        </div>

        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[280px] flex-1">
              <label htmlFor="hook-url" className="label">
                Endpoint URL
              </label>
              <input
                id="hook-url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://api.yourdomain.com/webhooks/riskpay"
                className="input"
              />
            </div>
            <div className="w-32">
              <label htmlFor="hook-mode" className="label">
                Mode
              </label>
              <select
                id="hook-mode"
                value={webhookMode}
                onChange={(e) => setWebhookMode(e.target.value as 'TEST' | 'LIVE')}
                className="input"
              >
                <option value="TEST">Test</option>
                <option value="LIVE">Live</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => createWebhook.mutate()}
              className="btn btn-primary"
              disabled={!webhookUrl.startsWith('https://') || createWebhook.isPending}
            >
              <Webhook className="h-4 w-4" aria-hidden="true" />
              Add endpoint
            </button>
          </div>
        </div>

        <ul className="mt-3 space-y-2">
          {(hooks.data?.data ?? []).map((hook) => (
            <li
              key={hook.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface-raised px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-content">{hook.url}</p>
                <p className="mt-1.5 flex flex-wrap items-center gap-2 text-2xs text-content-faint">
                  <span className={`badge ${hook.mode === 'LIVE' ? 'badge-success' : 'badge-neutral'}`}>
                    {hook.mode.toLowerCase()}
                  </span>
                  {hook.is_active ? (
                    <span className="badge badge-success">active</span>
                  ) : (
                    <span className="badge badge-danger">disabled</span>
                  )}
                  {hook.consecutive_failures > 0 && (
                    <span className="badge badge-warning">{hook.consecutive_failures} consecutive failures</span>
                  )}
                </p>
              </div>
              <button type="button" onClick={() => rotateSecret.mutate(hook.id)} className="btn btn-secondary btn-sm">
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Rotate secret
              </button>
            </li>
          ))}
          {(hooks.data?.data ?? []).length === 0 && !hooks.isLoading && (
            <li className="panel">
              <EmptyState
                title="No endpoints configured"
                description="Without a webhook you have to poll for status — which misses UPI approvals that land after the customer closes the page."
              />
            </li>
          )}
        </ul>
      </section>

      {/* ---- Return-URL verification ------------------------------------- */}
      <section>
        <div className="mb-4 border-b border-line pb-3">
          <h2 className="text-lg font-semibold tracking-tight">Return-URL signing key</h2>
          <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-content-muted">
            When hosted checkout sends a customer back to your{' '}
            <code className="numeral text-xs text-content-subtle">return_url</code>, the result rides in the query
            string and is signed with this key. Verify it before you show anyone a receipt — a browser redirect is
            typed by whoever holds the keyboard.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-line bg-surface-raised px-4 py-3.5">
          <p className="text-xs leading-relaxed text-content-subtle">
            HMAC-SHA256 over every parameter except <code className="numeral">signature</code>, sorted by name and
            joined as <code className="numeral">k=v</code> with <code className="numeral">&amp;</code>.
          </p>
          <button type="button" onClick={() => returnKey.mutate()} className="btn btn-secondary btn-sm" disabled={returnKey.isPending}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            Reveal key
          </button>
        </div>
      </section>

      {/* ---- Deliveries -------------------------------------------------- */}
      <section>
        <h2 className="mb-4 border-b border-line pb-3 text-lg font-semibold tracking-tight">Recent deliveries</h2>
        <DataTable
          columns={[
            { key: 'event', header: 'Event', render: (d: Delivery) => <span className="numeral text-xs">{d.event_type}</span> },
            { key: 'status', header: 'Status', render: (d: Delivery) => <StatusBadge status={d.status} /> },
            { key: 'http', header: 'HTTP', render: (d: Delivery) => <span className="numeral text-xs text-content-muted">{d.response_status ?? '—'}</span> },
            { key: 'attempt', header: 'Attempt', align: 'right', render: (d: Delivery) => <span className="numeral text-xs">{d.attempt}</span> },
            { key: 'duration', header: 'Duration', align: 'right', render: (d: Delivery) => <span className="numeral text-xs text-content-subtle">{d.duration_ms ? `${d.duration_ms}ms` : '—'}</span> },
            { key: 'when', header: 'When', align: 'right', render: (d: Delivery) => <span className="text-xs text-content-subtle">{relativeTime(d.created_at)}</span> },
          ]}
          rows={deliveries.data?.data ?? []}
          rowKey={(d) => d.id}
          loading={deliveries.isLoading}
          empty={<EmptyState title="No deliveries yet" description="Trigger a sandbox payment to see one land here." />}
        />
      </section>

      {session?.mode === 'LIVE' && (
        <p className="border-t border-line pt-4 text-xs leading-relaxed text-content-faint">
          You are viewing live data. Test and live keys address separate namespaces — an id created with one does not
          exist for the other.
        </p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Create key
 *
 *  The old version was two buttons that minted a key on click with a generated
 *  label and every scope. That is fast and wrong: nobody could tell two keys
 *  apart a month later, and a key that can move money out was one accidental
 *  click away.
 * ------------------------------------------------------------------------ */

function CreateKey({
  availableScopes,
  onClose,
  onCreated,
}: {
  availableScopes: string[];
  onClose: () => void;
  onCreated: (secret: string, label: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'TEST' | 'LIVE'>('TEST');
  const [full, setFull] = useState(true);
  const [scopes, setScopes] = useState<string[]>(['payments:read', 'payments:write']);
  const [error, setError] = useState<string | null>(null);

  const scopeList = availableScopes.length > 0 ? availableScopes : Object.keys(SCOPE_LABELS);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ secret_key: string }>('/developer/keys', {
        mode,
        label: label.trim() || `${mode === 'LIVE' ? 'Live' : 'Test'} key`,
        ...(full ? {} : { scopes }),
      }),
    onSuccess: (result) => onCreated(result.secret_key, label.trim() || `${mode === 'LIVE' ? 'Live' : 'Test'} key`),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not create the key.'),
  });

  const toggle = (scope: string) =>
    setScopes((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
      className="mb-5 rounded-lg border border-line bg-surface-raised p-5"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md border border-line bg-surface-inset">
            <KeyRound className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-sm font-medium text-content">New API key</h3>
            <p className="mt-1 text-xs text-content-subtle">The secret is shown once, immediately after creation.</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost h-7 w-7 p-0" aria-label="Cancel">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="key-label" className="label">
            Label
          </label>
          <input
            id="key-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Checkout server · production"
            maxLength={120}
            className="input"
          />
          <p className="mt-1.5 text-2xs leading-relaxed text-content-faint">
            Name it after where it will run. In six months this is the only way to know which key to revoke.
          </p>
        </div>

        <div>
          <span className="label">Mode</span>
          <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Key mode">
            {(['TEST', 'LIVE'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                aria-pressed={mode === value}
                className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === value
                    ? value === 'LIVE'
                      ? 'bg-brand-500 text-[#03150E]'
                      : 'bg-surface-overlay text-content'
                    : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {value === 'TEST' ? 'Test' : 'Live'}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-2xs leading-relaxed text-content-faint">
            {mode === 'TEST'
              ? 'Simulated money. Safe to paste into a scratch file.'
              : 'Real money. Live keys require an approved account.'}
          </p>
        </div>
      </div>

      <fieldset className="mt-5 border-t border-line pt-4">
        <legend className="sr-only">Permissions</legend>
        <label className="flex cursor-pointer items-start gap-2.5">
          <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} className="mt-0.5" />
          <span>
            <span className="text-sm text-content">Full access</span>
            <span className="mt-0.5 block text-xs text-content-subtle">
              Every current and future scope. Right for a first integration, wrong for a key you hand to a third party.
            </span>
          </span>
        </label>

        {!full && (
          <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {scopeList.map((scope) => (
              <label
                key={scope}
                className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 transition-colors ${
                  scopes.includes(scope) ? 'border-line-strong bg-surface-inset' : 'border-line hover:border-line-strong'
                }`}
              >
                <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggle(scope)} className="mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-xs text-content">{SCOPE_LABELS[scope] ?? scope}</span>
                  <span className="numeral mt-0.5 block text-2xs text-content-faint">{scope}</span>
                  {DANGEROUS.has(scope) && (
                    <span className="mt-1 block text-2xs text-[#FCD34D]">Can send funds to a beneficiary.</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={create.isPending || (!full && scopes.length === 0)}
        >
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          Create {mode === 'LIVE' ? 'live' : 'test'} key
        </button>
      </div>
    </form>
  );
}
